import { promises } from 'fs'
import { join } from 'path'

/**
 * Recursively read directory
 * @param  {string} dir Directory to read
 * @param  {RegExp} filter Filter for the file name, only the name part is considered, not the full path
 * @param  {string[]=[]} arr This doesn't have to be provided, it's used for the recursion
 * @param  {string=dir`} rootDir Used to replace the initial path, only the relative path is left, it's faster than path.relative.
 * @returns Promise array holding all relative paths
 */
export async function recursiveReadDir(
  dir: string,
  filter: RegExp,
  ignore?: RegExp,
  arr: string[] = [],
  rootDir: string = dir
): Promise<string[]> {
  const result = await promises.readdir(dir)

  await Promise.all(
    result.map(async (part: string) => {
      const absolutePath = join(dir, part)
      if (ignore && ignore.test(part)) return

      const pathStat = await promises.stat(absolutePath)

      if (pathStat.isDirectory()) {
        await recursiveReadDir(absolutePath, filter, ignore, arr, rootDir)
        return
      }

      if (!filter.test(part)) {
        return
      }

      arr.push(absolutePath.replace(rootDir, ''))
    })
  )

  return arr.sort()
}

export async function recursiveReadDirs(
  dirs: string[],
  filter: RegExp,
  ignore?: RegExp
): Promise<string[]> {
  const results = await Promise.all(
    dirs.map((directory) => {
      return recursiveReadDir(directory, filter, ignore)
    })
  )
  return [
    ...results.reduce((result, next) => {
      next.forEach(result.add, result)
      return result
    }, new Set<string>()),
  ]
}
